import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Folder, Users } from 'lucide-react'
import type { FolderScope } from '@/api/boardsApi'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * New folder (name + who sees it) or rename one (`folder` given). The team
 * choice is offered only when the person is in a team.
 */
export function FolderDialog({ open, folder, team, defaultScope = 'personal', pending, error, onOpenChange, onSubmit }: {
  open: boolean
  folder: { name: string } | null
  team: { name: string } | null
  defaultScope?: FolderScope
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onSubmit: (input: { name: string; scope: FolderScope }) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<FolderScope>(defaultScope)
  useEffect(() => {
    if (!open) return
    setName(folder?.name ?? '')
    setScope(team ? defaultScope : 'personal')
  }, [open, folder, team, defaultScope])

  const options: { value: FolderScope; icon: typeof Folder; title: string; help: string; disabled: boolean }[] = [
    { value: 'personal', icon: Folder, title: t('boards.folders.newMine'), help: t('boards.folders.newMineHelp'), disabled: false },
    {
      value: 'team',
      icon: Users,
      title: t('boards.folders.newTeam'),
      help: team ? t('boards.folders.newTeamHelp', { team: team.name }) : t('boards.folders.noTeam'),
      disabled: !team,
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{folder ? t('boards.folders.renameTitle', { name: folder.name }) : t('boards.folders.createTitle')}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) onSubmit({ name: name.trim(), scope })
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="folder-name">{t('boards.folders.name')}</Label>
            <Input id="folder-name" autoFocus maxLength={60} value={name} placeholder={t('boards.folders.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
          </div>
          {folder ? null : (
            <div role="radiogroup" aria-label={t('boards.folders.scope')} className="grid grid-cols-2 gap-2">
              {options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={scope === o.value}
                  disabled={o.disabled}
                  onClick={() => setScope(o.value)}
                  className={cn(
                    'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                    scope === o.value ? 'border-primary bg-accent' : 'border-line-strong hover:border-primary/60',
                  )}
                >
                  <span className="flex items-center gap-1.5 text-meta font-semibold text-ink">
                    <o.icon className="size-4 text-primary" aria-hidden />
                    {o.title}
                  </span>
                  <span className="text-label text-ink-muted">{o.help}</span>
                </button>
              ))}
            </div>
          )}
          {error ? (
            <p role="alert" className="text-meta text-destructive">
              {errorMessage(error)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {folder ? t('boards.folders.rename') : t('boards.folders.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
